/** SUNTRONIX ONE — Excel Migration Import Centre.
 *  Upload → Analyse sheets → Map → Validate → Import → Summary,
 *  original workbook preserved, per-sheet reverse. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, ArrowLeft, Loader2, CheckCircle2,
  AlertTriangle, XCircle, Copy, RotateCcw, History, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  VALID: { icon: CheckCircle2, cls: "text-emerald-600" },
  WARNING: { icon: AlertTriangle, cls: "text-amber-600" },
  ERROR: { icon: XCircle, cls: "text-red-600" },
  DUPLICATE: { icon: Copy, cls: "text-indigo-600" },
};

export default function ImportCentre() {
  const [view, setView] = useState("upload");        // upload | sheets | map | history
  const [batch, setBatch] = useState(null);
  const [active, setActive] = useState(null);        // active sheet meta
  const [destinations, setDestinations] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/excel-import/destinations").then(({ data }) => setDestinations(data.destinations || []));
  }, []);
  const loadHistory = () => api.get("/excel-import/history").then(({ data }) => setHistory(data.items || []));

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/excel-import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data);
      setView("sheets");
      toast.success(`${data.sheets.length} sheet(s) detected in ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-centre">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <FileSpreadsheet className="h-6 w-6" strokeWidth={1.7} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--st-text)]">Import Centre</h1>
          <p className="text-sm text-[color:var(--st-text-muted)]">Migrate legacy Excel / CSV into ONE — preview and confirm before anything is written.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" data-testid="import-history-tab" onClick={() => { loadHistory(); setView("history"); }}>
            <History className="h-4 w-4 mr-1.5" /> History
          </Button>
          {view !== "upload" && (
            <Button variant="outline" data-testid="import-new-btn" onClick={() => { setBatch(null); setActive(null); setView("upload"); }}>
              New import
            </Button>
          )}
        </div>
      </div>

      {view === "upload" && (
        <div
          data-testid="import-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className={"cursor-pointer rounded-2xl border-2 border-dashed p-16 text-center transition-colors " +
            (drag ? "border-[color:var(--st-accent)] bg-[color:var(--st-surface-alt)]" : "border-[color:var(--st-border)] hover:border-[color:var(--st-accent)]")}
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
            data-testid="import-file-input" onChange={(e) => doUpload(e.target.files?.[0])} />
          {busy ? <Loader2 className="h-10 w-10 mx-auto animate-spin text-[color:var(--st-accent)]" />
            : <UploadCloud className="h-12 w-12 mx-auto text-[color:var(--st-text-subtle)]" strokeWidth={1.4} />}
          <div className="mt-4 text-lg font-medium text-[color:var(--st-text)]">Drag an Excel file here</div>
          <div className="text-sm text-[color:var(--st-text-muted)] mt-1">or click to select · XLSX, XLS or CSV · multiple sheets supported</div>
        </div>
      )}

      {view === "sheets" && batch && (
        <div className="space-y-4" data-testid="import-sheets">
          <div className="text-sm text-[color:var(--st-text-muted)]">Workbook <span className="font-medium text-[color:var(--st-text)]">{batch.filename}</span> · original stored as source evidence. Choose a destination per sheet.</div>
          {batch.sheets.map((s) => (
            <SheetCard key={s.name} sheet={s} destinations={destinations}
              onMap={(dest) => { setActive({ ...s, destination: dest }); setView("map"); }} />
          ))}
        </div>
      )}

      {view === "map" && active && (
        <MapAndImport batchId={batch.batch_id} sheet={active} destinations={destinations}
          onDone={() => { setView("sheets"); }} onBack={() => setView("sheets")} />
      )}

      {view === "history" && (
        <HistoryList items={history} onReverse={async (bid, sheet) => {
          if (!window.confirm(`Reverse the import of sheet "${sheet}"? This deletes the records it created.`)) return;
          try {
            const { data } = await api.post(`/excel-import/batch/${bid}/reverse`, { sheet });
            toast.success(`Reversed — ${data.deleted} record(s) removed`);
            loadHistory();
          } catch (e) { toast.error(e?.response?.data?.detail || "Reverse failed"); }
        }} />
      )}
    </div>
  );
}

function SheetCard({ sheet, destinations, onMap }) {
  const [dest, setDest] = useState(sheet.importable ? (sheet.suggested_destination || "") : "");
  const importable = destinations.some((d) => d.key === sheet.suggested_destination);
  return (
    <div className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4 flex flex-wrap items-center gap-4" data-testid={`import-sheet-${sheet.name}`}>
      <div className="min-w-[180px]">
        <div className="font-medium text-[color:var(--st-text)]">{sheet.name}</div>
        <div className="text-xs text-[color:var(--st-text-subtle)]">{sheet.row_count} rows · {sheet.columns.length} columns</div>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] uppercase tracking-wide text-[color:var(--st-text-subtle)] mb-1">Suggested destination</div>
        {sheet.suggested_destination
          ? <span className={"inline-flex items-center gap-1.5 text-sm " + (importable ? "text-[color:var(--st-text)]" : "text-amber-600")}>
              <Sparkles className="h-3.5 w-3.5" /> {sheet.suggested_destination} · {sheet.confidence}
              {!importable && <span className="text-xs">(detected — not yet importable)</span>}
            </span>
          : <span className="text-sm text-[color:var(--st-text-subtle)]">Not detected — pick manually</span>}
      </div>
      <div className="flex items-center gap-2">
        <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid={`import-dest-select-${sheet.name}`}
          className="rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 py-2 text-sm">
          <option value="">Choose destination…</option>
          {destinations.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        <Button disabled={!dest} data-testid={`import-map-btn-${sheet.name}`} onClick={() => onMap(dest)}>Map columns</Button>
      </div>
    </div>
  );
}

function MapAndImport({ batchId, sheet, destinations, onDone, onBack }) {
  const destCfg = destinations.find((d) => d.key === sheet.destination);
  const [mapping, setMapping] = useState({});
  const [columns, setColumns] = useState(sheet.columns || []);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dupStrategy, setDupStrategy] = useState("SKIP");

  useEffect(() => {
    api.get(`/excel-import/batch/${batchId}/automap`, { params: { sheet: sheet.name, destination: sheet.destination } })
      .then(({ data }) => { setMapping(data.mapping || {}); setColumns(data.columns || []); })
      .catch(() => {});
  }, [batchId, sheet]);

  const doValidate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/excel-import/batch/${batchId}/validate`, { sheet: sheet.name, destination: sheet.destination, mapping });
      setValidation(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Validation failed"); }
    finally { setBusy(false); }
  };
  const doCommit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/excel-import/batch/${batchId}/commit`, { sheet: sheet.name, destination: sheet.destination, mapping, dup_strategy: dupStrategy });
      setResult(data.summary);
      toast.success(`Imported ${data.summary.created} record(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-map">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
        <ArrowLeft className="h-4 w-4" /> Back to sheets
      </button>
      <div className="text-lg font-semibold text-[color:var(--st-text)]">{sheet.name} → {destCfg?.label}</div>

      {/* Column mapping */}
      <div className="rounded-xl border border-[color:var(--st-border)] overflow-hidden">
        <div className="grid grid-cols-2 bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wide text-[color:var(--st-text-subtle)] px-4 py-2">
          <div>ONE field</div><div>Excel column</div>
        </div>
        {(destCfg?.fields || []).map((f) => (
          <div key={f.key} className="grid grid-cols-2 items-center px-4 py-2 border-t border-[color:var(--st-border)]">
            <div className="text-sm text-[color:var(--st-text)]">{f.label}{f.required && <span className="text-red-500"> *</span>}</div>
            <select value={mapping[f.key] || ""} data-testid={`import-map-${f.key}`}
              onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
              className="rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-2 py-1.5 text-sm">
              <option value="">— not mapped —</option>
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={doValidate} disabled={busy} data-testid="import-validate-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Preview & validate
        </Button>
        {validation && !result && (
          <>
            <label className="text-sm text-[color:var(--st-text-muted)]">Duplicates:</label>
            <select value={dupStrategy} onChange={(e) => setDupStrategy(e.target.value)} className="rounded-md border border-[color:var(--st-border)] px-2 py-1.5 text-sm" data-testid="import-dup-strategy">
              <option value="SKIP">Skip existing</option>
              <option value="CREATE">Create anyway</option>
            </select>
            <Button onClick={doCommit} disabled={busy} data-testid="import-commit-btn">Import now</Button>
          </>
        )}
      </div>

      {/* Validation staging */}
      {validation && !result && (
        <div className="space-y-3" data-testid="import-validation">
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(validation.counts).map(([k, v]) => {
              const St = STATUS_STYLE[k]; const Icon = St?.icon || CheckCircle2;
              return <span key={k} className={"inline-flex items-center gap-1.5 rounded-full bg-[color:var(--st-surface-alt)] px-3 py-1 " + (St?.cls || "")}><Icon className="h-4 w-4" /> {k}: {v}</span>;
            })}
          </div>
          <div className="rounded-xl border border-[color:var(--st-border)] overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[color:var(--st-surface-alt)]">
                <tr>
                  <th className="text-left px-3 py-2">Row</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Detail</th>
                  <th className="text-left px-3 py-2">Data</th>
                </tr>
              </thead>
              <tbody>
                {validation.preview.map((r) => {
                  const St = STATUS_STYLE[r.status]; const Icon = St?.icon || CheckCircle2;
                  return (
                    <tr key={r.row} className="border-t border-[color:var(--st-border)]">
                      <td className="px-3 py-1.5 text-[color:var(--st-text-subtle)]">{r.row}</td>
                      <td className={"px-3 py-1.5 " + (St?.cls || "")}><span className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {r.status}</span></td>
                      <td className="px-3 py-1.5 text-[color:var(--st-text-muted)]">{r.reason || "—"}</td>
                      <td className="px-3 py-1.5 text-[color:var(--st-text)] truncate max-w-[420px]">{Object.values(r.data).filter(Boolean).slice(0, 4).join(" · ")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="import-summary">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" /> Import complete</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Received" v={result.received} />
            <Stat label="Created" v={result.created} />
            <Stat label="Duplicates" v={result.duplicates} />
            <Stat label="Skipped" v={result.skipped} />
            <Stat label="Failed" v={result.failed} />
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 text-xs text-red-700">
              {result.errors.slice(0, 8).map((e, i) => <div key={i}>Row {e.row}: {e.reason}</div>)}
            </div>
          )}
          <Button className="mt-4" onClick={onDone} data-testid="import-done-btn">Done</Button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }) {
  return <div className="rounded-lg bg-white/70 px-3 py-2"><div className="text-[11px] uppercase tracking-wide text-[color:var(--st-text-subtle)]">{label}</div><div className="text-lg font-semibold text-[color:var(--st-text)]">{v}</div></div>;
}

function HistoryList({ items, onReverse }) {
  if (!items.length) return <div className="text-sm text-[color:var(--st-text-muted)]" data-testid="import-history-empty">No imports yet.</div>;
  return (
    <div className="space-y-3" data-testid="import-history">
      {items.map((b) => (
        <div key={b.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-[color:var(--st-text-subtle)]" />
            <span className="font-medium text-[color:var(--st-text)]">{b.filename}</span>
            <span className="text-xs text-[color:var(--st-text-subtle)]">{b.code} · {new Date(b.uploaded_at).toLocaleString()}</span>
          </div>
          <div className="mt-2 space-y-1.5">
            {Object.entries(b.results || {}).map(([sheet, r]) => (
              <div key={sheet} className="flex items-center gap-2 text-sm">
                <span className="text-[color:var(--st-text)]">{sheet} → {r.destination}</span>
                <span className="text-[color:var(--st-text-muted)]">created {r.summary?.created}, dup {r.summary?.duplicates}, failed {r.summary?.failed}</span>
                {r.reversed
                  ? <span className="text-xs text-[color:var(--st-text-subtle)]">· reversed</span>
                  : <button onClick={() => onReverse(b.id, sheet)} data-testid={`import-reverse-${sheet}`} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><RotateCcw className="h-3.5 w-3.5" /> Reverse</button>}
              </div>
            ))}
            {Object.keys(b.results || {}).length === 0 && <div className="text-xs text-[color:var(--st-text-subtle)]">Analysed, not yet imported.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
